import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn("animate-spin text-muted", className)} size={20} />;
}

export function PageLoading() {
  return (
    <div className="flex h-64 items-center justify-center">
      <Spinner />
    </div>
  );
}

/**
 * Sahifa sarlavhasi (redesign v2): ixtiyoriy kichik yuqori yozuv (eyebrow), katta nom, izoh, o'ngda amallar.
 * `children` -- filtrlar qatori (sarlavha ostida).
 */
export function PageHeader({ title, subtitle, eyebrow, actions, children, className }: { title: ReactNode; subtitle?: ReactNode; eyebrow?: ReactNode; actions?: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <div className={cn("mb-5", className)}>
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
        <div className="min-w-0">
          {eyebrow && <div className="mb-1 text-[12px] font-semibold tracking-[0.06em] text-muted uppercase">{eyebrow}</div>}
          <h1 className="text-[30px] leading-[1.1] font-bold tracking-[-0.025em]">{title}</h1>
          {subtitle && <div className="mt-1.5 text-[14px] text-muted">{subtitle}</div>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export function EmptyState({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-card border border-dashed border-border px-6 py-14 text-center">
      <div className="text-[15px] font-semibold">{title}</div>
      {description && <div className="mt-1 max-w-md text-[13px] text-muted">{description}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-card border border-danger/40 bg-danger-soft px-4 py-3 text-[13px]">
      <span className="font-semibold text-danger">{t("states.error")}</span> {message}
      {onRetry && (
        <button onClick={onRetry} className="ml-2 text-primary hover:underline">
          {t("actions.retry")}
        </button>
      )}
    </div>
  );
}

export function Avatar({ name, initials, size = 32, className, tone = "fill" }: { name?: string; initials: string; size?: number; className?: string; tone?: "fill" | "ink" | "accent" }) {
  const tones = { fill: "bg-fill text-ink", ink: "bg-ink text-surface", accent: "bg-accent text-on-accent" };
  return (
    <span
      title={name}
      style={{ width: size, height: size, fontSize: Math.round(size / 2.7) }}
      className={cn("inline-flex shrink-0 items-center justify-center rounded-full font-semibold tracking-[0.02em]", tones[tone], className)}
    >
      {initials}
    </span>
  );
}

export function Kv({ label, children, tone }: { label: string; children: ReactNode; tone?: "muted" | "warning" }) {
  return (
    <div className="grid grid-cols-[170px_1fr] items-start py-1.5 text-[13px]">
      <div className="text-muted">{label}</div>
      <div className={cn(tone === "muted" && "text-muted", tone === "warning" && "font-semibold text-warning")}>{children}</div>
    </div>
  );
}

export function Divider({ className }: { className?: string }) {
  return <hr className={cn("border-line", className)} />;
}

/** Kichik sarlavha (karta ichidagi bo'lim) */
export function SectionLabel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("text-[11px] font-semibold tracking-[0.06em] text-muted uppercase", className)}>{children}</div>;
}

/** Qidiruv aniq topmadi -- o'xshashlari ko'rsatilmoqda (backend javobida `fuzzy`, core.textsearch / lib/textsearch) */
export function SimilarNote({ show, className }: { show?: boolean; className?: string }) {
  const { t } = useTranslation();
  if (!show) return null;
  return <p className={cn("mb-2 text-[12px] text-muted", className)}>{t("select.similar")}</p>;
}

